import { Textarea } from "@stardust-ds/react"
import { forwardRef, useCallback, useContext, useEffect, useRef, useState } from "react"
import CalcIcon from "../../../assets/icons/CalcIcon"
import MoveEllipsisIcon from "../../../assets/icons/MoveEllipsisIcon"
import BoardContext from "../../../contexts/Board"
import FieldsContext from "../../../contexts/Fields"
import Label from "../../atoms/Label"
import styles from "./index.module.scss"

import {
  calculate,
  changeVariablesToValueOnFormula,
  checkIfFormulaIsCorrect,
  getCurrentField,
  getVariablesOnFormula,
  textToFormula,
} from "./logic"

const aSpecialCharacters = ["+", "-", "/", "*", "(", ")", "{", "}", "[", "]"]

const CalcField = forwardRef((props, ref) => {
  const {
    bIsEditModel,
    value,
    setValue,
    cFieldName,
    getValues,
    cActivityFormula,
    oActivityFields,
    activityFieldsValues,
    openShowCalc,
    recalculate,
    setError,
    clearErrors,
    watch,
    aFields,
  } = props
  const [selectedCharacter, setSelectedCharacter] = useState(null)
  const [inputText, setInputText] = useState("")
  const [showOptions, setShowOptions] = useState(false)
  const [showFields, setShowFields] = useState(false)
  const [numField, setNumField] = useState([])
  const [textWithSpans, setTextWithSpans] = useState("")
  const [cInputNames, setInputNames] = useState([])
  const [cFormula, setFormula] = useState("")
  const [cModelFormula, setCModelFormula] = useState("")

  const inputRef = useRef(null)

  const { aAllFields } = useContext(FieldsContext)
  const { oCurrentBoard } = useContext(BoardContext)

  useEffect(() => {
    const ModelBoard = oCurrentBoard.board_model || oCurrentBoard.data[0].board_model
    const aFieldName = cFieldName.split("-")
    const nFieldId = aFieldName[aFieldName.length - 1]

    const currentField = getCurrentField(ModelBoard, nFieldId)
    const formula = currentField ? currentField.ombf_cvalue : ""

    if (cModelFormula === formula) return
    setCModelFormula(formula)
    if (cActivityFormula) {
      setFormula(cActivityFormula)
    } else {
      setFormula(formula)
    }

    const inputIds = formula ? getVariablesOnFormula(formula) : []
    const inputNames = inputIds.map((field) => `activity-field-${field}`)

    setInputNames(inputNames)
    run()
  }, [cFormula])

  useEffect(() => {
    if (!value) return
    setInputText(() => value.value ?? value)
    handleFormatText(value.value ?? value)
  }, [value])

  useEffect(() => {
    clearErrors()

    if (bIsEditModel && !checkIfFormulaIsCorrect(inputText, getValues)) {
      setError(cFieldName, {
        type: "manual",
        message: "",
      })
    }
  }, [inputText])

  useEffect(() => {
    if (bIsEditModel || !cInputNames?.length) return

    run()
    const subscription = watch((_, { name }) => {
      if (name == cFieldName) return
      if (cInputNames.includes(name)) {
        run()
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, bIsEditModel, cInputNames])

  const run = () => {
    try {
      const newFormula = changeVariablesToValueOnFormula(cFormula, aAllFields, getValues)

      let nDecimals = aAllFields?.filter(
        (field) =>
          field.opfd_nid ===
          Number(cFieldName.split("-")[cFieldName.split("-").length - 1]),
      )[0].opfd_nprecision
      if (!newFormula) {
        handleFormatText("")
        return
      }
      calculate(newFormula, (resp) => {
        const value =
          (resp && String(Number(resp).toFixed(nDecimals).replace(".", ","))) || "0"
        handleFormatText(value)
      })
    } catch (error) {
      console.log(error)
    }
  }

  const handleTextareaChange = (event) => {
    setShowOptions(false)
    event.target.value = event.target.value.replace(/\s/g, "")
    setInputText(() => event.target.value)
    handleFormatText(event.target.value)
  }
  const filterFieldByName = (text) => {
    if (!text) return []
    const fields = getAllNumberFields()
    const aFilteredFields = fields.filter((field) => {
      return field.opfd_cname.toLowerCase().includes(text.toLowerCase())
    })
    return aFilteredFields
  }
  const getAllNumberFields = () => {
    return (
      aFields &&
      aFields.filter((item) => item.opfd_ctype === "NUMBER" || item.opfd_bcalculate)
    )
  }
  const handleShowFOptions = () => {
    setShowOptions((prev) => !prev)
  }
  const handleOperationClick = (operation) => {
    const newText = inputText + operation
    setInputText(newText)
    handleFormatText(newText)
    setSelectedCharacter(null)
    setShowOptions(false)
    setShowFields(false)
    inputRef.current.focus()
  }
  const handleOptionClick = (fieldId) => {
    // Encontra o último operador ou agrupador
    const regex = /[+\-*/{}()[\]]/g
    const inputArr = inputText.split(regex)
    const lastCharacter = inputArr[inputArr.length - 1]

    // substitui o ultimo campo pelo campo selecionado
    const newText = inputText.replace(lastCharacter, `'${fieldId}'`)
    setInputText(newText)
    handleFormatText(newText)
    setSelectedCharacter(null)
    setShowOptions(false)
    setShowFields(false)
    inputRef.current.focus()
  }
  const handleShowFields = (field, buffer) => {
    setNumField(filterFieldByName(buffer))
    setShowFields(true)
  }
  const handleCreateTokens = (formulaObj, styled) => {
    return formulaObj.map((item) => ({
      ...item,
      span: `<span class="${styles.badge} ${styled[item.type.toLowerCase()]}" ${
        item.type === "FIELD" && 'data-type="valid"'
      }>${item.label}</span>`,
    }))
  }
  const handleFormatText = (text) => {
    const fieldValue = getValues(cFieldName)
    if (fieldValue === text) return
    setShowFields(() => false)
    setShowOptions(() => false)

    const formulaObj = textToFormula(
      text,
      getAllNumberFields() /* props.aFields */,
      handleShowFields,
    )
    const styled = {
      operator: styles.operator,
      number: styles.number,
      braces: styles.braces,
      brackets: styles.brackets,
      parentheses: styles.parentheses,
      variable: styles.variable,
      invalid: styles.invalid,
      field: styles.variable,
    }
    const tokenizedText = handleCreateTokens(formulaObj, styled)

    let processedText = text
    processedText = tokenizedText.map((token) => token.span).join("")

    setTextWithSpans(() => processedText)

    const calcValue = bIsEditModel ? text : { value: text, formula: cFormula }

    if (bIsEditModel && fieldValue == text) return
    if (
      !bIsEditModel &&
      fieldValue?.value == calcValue?.value &&
      fieldValue?.cFormula == calcValue.cFormula
    )
      return

    setValue?.(cFieldName, calcValue)
  }
  return (
    <div>
      <div className={styles.draggableComponent}>
        <MoveEllipsisIcon />
        <Label className={styles.label}>{props.label}</Label>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
        <div className={styles.fakeInput + " " + (!bIsEditModel ? styles.editMode : "")}>
          <div
            className={styles.fakeInputDisplay}
            dangerouslySetInnerHTML={{ __html: textWithSpans }}
          />

          <div>
            <textarea
              ref={inputRef}
              onChange={handleTextareaChange}
              onKeyDown={(e) => {
                if (e.key === "Backspace") {
                  // verifique se o ultimo caractere do inputText é uma aspa simples
                  if (inputText.slice(-1) === "'") {
                    e.preventDefault()
                    // imagine que eu tenho uma string da seguinte forma '10'+29*'19'. Crie uma regex que pega apenas a ultima ocorrencia de um numero entre aspas simples, que nesse exemplo é '19'
                    const regex = /'([^']+)'/g
                    // faça um match com a regex
                    const matches = inputText.match(regex)
                    // se houver um match
                    if (matches && matches.length > 0) {
                      // pegue o valor do último match
                      const value = matches[matches.length - 1].replace(/'/g, "")
                      // encontre o índice da última ocorrência
                      const lastIndex = inputText.lastIndexOf(`'${value}'`)
                      // crie uma nova string que substitui o valor do match por um espaço em branco
                      const newText =
                        inputText.slice(0, lastIndex) +
                        inputText.slice(lastIndex + value.length + 2)
                      // sete o novo valor do inputText
                      setInputText(newText)
                      // formate o texto
                      handleFormatText(newText)
                    }
                  }
                }
              }}
              value={inputText}
              className={styles.invisibleInput}
              disabled={!bIsEditModel}
              spellCheck={false}
            />

            {bIsEditModel && (
              <CalcIcon
                width={20}
                height={20}
                className={styles.iconRight}
                onClick={handleShowFOptions}
              />
            )}
          </div>
        </div>
        {!bIsEditModel && (
          <>
            <button
              style={{
                padding: "8px",
                borderRadius: "100%",
                border: "1px solid #415364",
              }}
              onClick={(e) => {
                e.preventDefault()
                openShowCalc()
              }}
            >
              <CalcIcon width={15} height={12} />
            </button>
            {cFormula !== cModelFormula && (
              <button
                style={{
                  padding: "4px 16px",
                  minHeight: "47px",
                  borderRadius: "500px",
                  border: "none",
                  backgroundColor: "#6CB6E3",
                  color: "white",
                  fontWeight: "bold",
                }}
                onClick={(e) => {
                  e.preventDefault()

                  setFormula(() => cModelFormula)
                }}
              >
                Recalcular
              </button>
            )}
          </>
        )}
      </div>

      {showOptions && (
        <div className={styles.calcOptions}>
          {aSpecialCharacters.map((char, index) => (
            <span key={index} onClick={() => handleOperationClick(char)}>
              {char}
            </span>
          ))}
        </div>
      )}
      {showFields && (
        <div className={styles.calcOptions}>
          {numField.map((field, index) => (
            <span key={index} onClick={() => handleOptionClick(field.opfd_nid)}>
              <strong>{field.opfd_nid} </strong> - {field.opfd_cname}
            </span>
          ))}
        </div>
      )}
    </div>
  )
})
function CalcSpan({ label, type }) {
  const styled = {
    operator: styles.operator,
    number: styles.number,
    braces: styles.braces,
    brackets: styles.brackets,
    parentheses: styles.parentheses,
    variable: styles.variable,
    invalid: styles.invalid,
  }
  return <span className={styled[type.toLowerCase()]}>{label}</span>
}

CalcField.displayName = "CalcField"

export default CalcField
